// App shell: navigation + main content, inside the Rumio iframe. Wide
// embeds get a sidebar; narrow ones (phones, small host columns) a top bar.

import { type ReactNode } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useMe } from '../lib/queries';

interface NavItem {
  label: string;
  to: string;
}

const NAV: NavItem[] = [
  { label: 'Dashboard', to: '/' },
  { label: 'Podcasts', to: '/podcasts' },
  { label: 'Studio', to: '/studio' },
  { label: 'Media', to: '/media' },
  { label: 'Sponsors', to: '/sponsors' },
  { label: 'Projects', to: '/projects' },
  { label: 'Activity', to: '/jobs' },
  { label: 'Settings', to: '/settings' },
];

function navClass({ isActive }: { isActive: boolean }): string {
  return [
    'block rounded-md px-3 py-2 text-sm transition-colors',
    isActive ? 'bg-accent-soft font-medium text-accent' : 'text-muted hover:bg-surface hover:text-text',
  ].join(' ');
}

function NavLinks(): ReactNode {
  return NAV.map((n) => (
    <li key={n.to}>
      <NavLink to={n.to} end={n.to === '/'} className={navClass}>
        {n.label}
      </NavLink>
    </li>
  ));
}

export function planLabel(slug: string): string {
  return `${slug.charAt(0).toUpperCase()}${slug.slice(1)} plan`;
}

function UserBadge(): ReactNode {
  const me = useMe();
  if (me.isLoading) return <div className="p-3 text-xs text-muted">…</div>;
  if (me.isError || !me.data) {
    return <div className="p-3 text-xs text-danger">Session expired. Reload the page from your site.</div>;
  }
  const u = me.data.user;
  return (
    <div className="border-t border-border p-3 text-xs text-muted">
      <div className="truncate font-medium text-text">{u.displayName ?? u.email ?? u.id ?? 'Signed in'}</div>
      <div>{planLabel(me.data.plan_slug)}</div>
    </div>
  );
}

export function Layout(): ReactNode {
  return (
    <div className="min-h-screen bg-bg md:flex">
      <aside className="hidden w-56 shrink-0 flex-col border-r border-border md:flex md:sticky md:top-0 md:h-screen">
        <div className="border-b border-border p-4">
          <span className="text-lg font-bold text-accent">PodcastWiz</span>
        </div>
        <nav aria-label="Main" className="flex-1 overflow-y-auto p-3">
          <ul className="space-y-1">
            <NavLinks />
          </ul>
        </nav>
        <UserBadge />
      </aside>
      <header className="border-b border-border md:hidden">
        <nav aria-label="Main" className="overflow-x-auto px-3 py-2">
          <ul className="flex gap-1 whitespace-nowrap">
            <NavLinks />
          </ul>
        </nav>
      </header>
      <main className="min-w-0 flex-1 p-4 md:p-8">
        <Outlet />
      </main>
    </div>
  );
}
